import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { api } from '../../lib/api'
import {
  FEEDBACK_KINDS,
  resolveFeedbackBoard,
  type FeedbackKind,
  type QuackbackBoardIds,
} from '../../lib/feedback/kinds'
import {
  subscribeQuackbackUnread,
  type QuackbackCommand,
} from './quackbackUnread'

type QuackbackSsoResponse = {
  /** Preferred (Quackback install skill). */
  ssoToken?: string
  sso_token?: string
  /** Legacy alias. */
  token?: string
  expires_in: number
  base_url: string
}

declare global {
  interface Window {
    Quackback?: QuackbackCommand
  }
}

function ssoTokenFrom(data: QuackbackSsoResponse): string | null {
  return data.ssoToken || data.sso_token || data.token || null
}

function loadSdk(baseUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-quackback-sdk]') as HTMLScriptElement | null
    const sdkReady = typeof window.Quackback === 'function' && !window.Quackback.q
    if (existing && sdkReady) {
      resolve()
      return
    }

    // Stale tag from a prior 404/hang — drop it so we can reload.
    if (existing && !sdkReady) {
      existing.remove()
    }

    // Queue stub (Canny/Quackback pattern) until sdk.js replaces it.
    if (typeof window.Quackback !== 'function') {
      const q: unknown[] = []
      const stub = ((...args: unknown[]) => {
        q.push(args)
      }) as QuackbackCommand
      stub.q = q
      window.Quackback = stub
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `${baseUrl.replace(/\/$/, '')}/api/widget/sdk.js`
    script.dataset.quackbackSdk = '1'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Quackback SDK'))
    document.head.appendChild(script)
  })
}

function isWidgetOpen(): boolean {
  try {
    return Boolean(window.Quackback?.('isOpen'))
  } catch {
    return false
  }
}

/**
 * Quackback's host panel has no background — while the iframe hydrates you see
 * the app through a floating empty frame (looks like a UI bug). Opaque fill
 * matches Quackback card chrome for light/dark.
 */
function ensureOpaquePanelChrome(): void {
  const id = 'ofertator-quackback-panel-fix'
  if (document.getElementById(id)) return
  const el = document.createElement('style')
  el.id = id
  el.textContent = [
    '.quackback-panel{background:#ffffff}',
    '.quackback-panel iframe{background:#ffffff}',
    '@media(prefers-color-scheme:dark){',
    '.quackback-panel{background:#0a0a0a}',
    '.quackback-panel iframe{background:#0a0a0a}',
    '}',
  ].join('')
  document.head.appendChild(el)
}

export type UseQuackbackWidgetOptions = {
  /** Public instance URL from feature flags (anonymous init / feedback for all). */
  publicBaseUrl?: string | null
  /** Mint SSO JWT + mirror unread (notifications-gated). */
  useSso?: boolean
  /** Board ids from system_config (via feature-flags). */
  boardIds?: QuackbackBoardIds | null
}

/**
 * Loads Quackback with launcher hidden (we open from Ofertator chrome).
 * Identify via server-minted SSO JWT when useSso; otherwise anonymous.
 *
 * News unread is Quackback-owned (seen marker in the widget iframe).
 * We mirror `Quackback.on('unread')` into the Ofertator bell — no Postgres
 * news rows. SDK count = widget aggregate (changelog + optional messenger/tickets);
 * with our tab set that is effectively changelog.
 */
export function useQuackbackWidget({
  publicBaseUrl = null,
  useSso = false,
  boardIds = null,
}: UseQuackbackWidgetOptions) {
  const user = useAuthStore((s) => s.user)
  const { i18n } = useTranslation()
  const [ready, setReady] = useState(false)
  const [baseUrl, setBaseUrl] = useState<string | null>(null)
  const [newsUnread, setNewsUnread] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const currentViewRef = useRef<string | null>(null)
  const initRef = useRef(false)
  const initLocaleRef = useRef<string | null>(null)
  const identifiedUserRef = useRef<number | null>(null)

  const enabled = Boolean(publicBaseUrl) || (useSso && Boolean(user))

  useEffect(() => {
    if (!enabled) {
      setReady(false)
      setNewsUnread(0)
      setIsOpen(false)
      return
    }

    let cancelled = false
    let unsubUnread: (() => void) | null = null
    let unsubOpen: (() => void) | null = null
    let unsubClose: (() => void) | null = null
    let unsubReady: (() => void) | null = null
    let readyFallbackTimer: ReturnType<typeof setTimeout> | null = null

    const subscribeLifecycle = () => {
      const qb = window.Quackback
      if (typeof qb !== 'function' || qb.q) return

      unsubUnread = subscribeQuackbackUnread(qb, (count) => {
        if (!cancelled) setNewsUnread(count)
      })

      const onOpen = (payload: unknown) => {
        if (cancelled) return
        const view =
          payload && typeof payload === 'object' && 'view' in payload
            ? String((payload as { view?: string }).view ?? '')
            : null
        currentViewRef.current = view || null
        setIsOpen(true)
      }
      const onClose = () => {
        if (cancelled) return
        currentViewRef.current = null
        setIsOpen(false)
      }
      const onReady = () => {
        if (cancelled) return
        if (readyFallbackTimer) {
          clearTimeout(readyFallbackTimer)
          readyFallbackTimer = null
        }
        setReady(true)
      }
      const offOpen = qb('on', 'open', onOpen)
      const offClose = qb('on', 'close', onClose)
      const offReady = qb('on', 'ready', onReady)
      unsubOpen = typeof offOpen === 'function' ? (offOpen as () => void) : () => qb('off', 'open', onOpen)
      unsubClose = typeof offClose === 'function' ? (offClose as () => void) : () => qb('off', 'close', onClose)
      unsubReady =
        typeof offReady === 'function' ? (offReady as () => void) : () => qb('off', 'ready', onReady)
      setIsOpen(isWidgetOpen())
    }

    ;(async () => {
      try {
        let instanceUrl = (publicBaseUrl || '').replace(/\/$/, '')
        let ssoToken: string | null = null

        if (useSso && user) {
          try {
            const { data } = await api.get<QuackbackSsoResponse>('/integrations/quackback/sso-token')
            if (cancelled) return
            ssoToken = ssoTokenFrom(data)
            if (data.base_url) {
              instanceUrl = data.base_url.replace(/\/$/, '')
            }
          } catch {
            // Fall back to anonymous widget when SSO is unavailable.
            ssoToken = null
          }
        }

        if (!instanceUrl) {
          if (!cancelled) setReady(false)
          return
        }

        setBaseUrl(instanceUrl)
        await loadSdk(instanceUrl)
        if (cancelled) return

        ensureOpaquePanelChrome()

        const locale = i18n.language || 'pl'
        if (!initRef.current || initLocaleRef.current !== locale) {
          window.Quackback?.('init', {
            instanceUrl,
            launcher: false,
            locale,
            ...(ssoToken ? { identity: { ssoToken } } : {}),
          })
          initRef.current = true
          initLocaleRef.current = locale
          identifiedUserRef.current = ssoToken && user ? user.id : null
        } else if (ssoToken && user && identifiedUserRef.current !== user.id) {
          window.Quackback?.('identify', { ssoToken })
          identifiedUserRef.current = user.id
        } else if (ssoToken) {
          window.Quackback?.('identify', { ssoToken })
        }

        subscribeLifecycle()
        // Prefer iframe `ready`; fall back so the button isn't stuck forever if
        // the event was missed (e.g. panel preloaded before we subscribed).
        readyFallbackTimer = setTimeout(() => {
          if (!cancelled) setReady(true)
        }, 2500)
      } catch {
        if (!cancelled) {
          setReady(false)
          setBaseUrl(null)
          setNewsUnread(0)
          setIsOpen(false)
        }
      }
    })()

    return () => {
      cancelled = true
      if (readyFallbackTimer) clearTimeout(readyFallbackTimer)
      unsubUnread?.()
      unsubOpen?.()
      unsubClose?.()
      unsubReady?.()
    }
  }, [enabled, publicBaseUrl, useSso, user?.id, i18n.language])

  const openChangelog = useCallback(() => {
    window.Quackback?.('open', { view: 'changelog' })
  }, [])

  const openFeedback = useCallback(
    (kind: FeedbackKind) => {
      const { titlePrefix } = FEEDBACK_KINDS[kind]
      currentViewRef.current = 'new-post'
      window.Quackback?.('open', {
        view: 'new-post',
        board: resolveFeedbackBoard(kind, boardIds),
        title: titlePrefix,
      })
    },
    [boardIds]
  )

  const close = useCallback(() => {
    window.Quackback?.('close')
  }, [])

  const openPortal = useCallback(() => {
    if (!baseUrl) return
    window.open(baseUrl.replace(/\/$/, ''), '_blank', 'noopener,noreferrer')
  }, [baseUrl])

  return {
    ready,
    baseUrl,
    newsUnread,
    isOpen,
    openChangelog,
    openFeedback,
    close,
    openPortal,
  }
}
